import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import { useAuth } from '../../contexts/AuthContext';
import { ViewComplaintDialog } from '../../components/dialogs';

const AllComplaintsView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userComplaints, setUserComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  // Fetch complaints from API on component mount
  useEffect(() => {
    const fetchComplaints = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await complaintService.getUserOnlineComplaints();
        if (response.success) {
          // Handle different response structures
          const complaints = response.data?.data || response.data || [];
          setUserComplaints(Array.isArray(complaints) ? complaints : []);
        } else {
          setError(response.message || 'Failed to fetch complaints');
          setUserComplaints([]);
        }
      } catch (err) {
        console.error('Error fetching complaints:', err);
        setError('Failed to load complaints. Please try again.');
        setUserComplaints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchComplaints();
  }, []);

  const handleCreateComplaint = () => {
    navigate('/vigilance-dashboard/create');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">All Complaints</h2>
              <p className="text-sm text-gray-600 mt-1">View and track your submitted complaints</p>
            </div>
            <button
              onClick={handleCreateComplaint}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Complaint
            </button>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="flex flex-col items-center">
              <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-600">Loading complaints...</p>
            </div>
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-red-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">Error loading complaints</p>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Complaint #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userComplaints.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-2">No complaints submitted yet</p>
                        <p className="text-gray-500 mb-4">Start by creating your first vigilance complaint</p>
                        <button
                          onClick={handleCreateComplaint}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Create New Complaint
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  userComplaints.map((complaint) => {
                    // Extract complaint data from API response
                    const complaintNo = complaint.complaint_no || complaint.complaint_number || '';
                    const complaintType = complaint.complaint_type || 'online';
                    const status = complaint.status || complaint.complaint_status || 'Pending';

                    // Format dates
                    const formatDate = (dateString) => {
                      if (!dateString) return 'N/A';
                      try {
                        const date = new Date(dateString);
                        return date.toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      } catch (e) {
                        return dateString;
                      }
                    };

                    const submittedDate = formatDate(complaint.submitted_date);
                    const lastDate = formatDate(complaint.last_date);
                    const address = complaint.address || 'N/A';

                    // Get status color
                    const getStatusColor = (statusValue) => {
                      const statusLower = (statusValue || '').toLowerCase();
                      if (statusLower.includes('investigation')) {
                        return 'bg-yellow-100 text-yellow-800';
                      } else if (statusLower.includes('resolved') || statusLower.includes('closed')) {
                        return 'bg-green-100 text-green-800';
                      } else if (statusLower.includes('pending')) {
                        return 'bg-blue-100 text-blue-800';
                      } else if (statusLower.includes('rejected') || statusLower.includes('cancelled')) {
                        return 'bg-red-100 text-red-800';
                      }
                      return 'bg-gray-100 text-gray-800';
                    };

                    return (
                      <tr key={complaintNo || Math.random()} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {complaintNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800 capitalize">
                            {complaintType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
                            {status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submittedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lastDate}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {address}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => setSelectedComplaint({
                                ...complaint,
                                complaint_id: complaintNo,
                                date: submittedDate,
                                documents: complaint.files || [],
                                name: user?.name || complaint.name || '',
                                mobile: user?.phone || user?.mobile || complaint.mobile || '',
                                email: user?.email || complaint.email || '',
                              })}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* View Dialog */}
        <ViewComplaintDialog
          isOpen={!!selectedComplaint}
          onClose={() => setSelectedComplaint(null)}
          complaint={selectedComplaint}
        />
      </div>
    </div>
  );
};

export default AllComplaintsView;
